"use client";

import React from "react";
import NextLink from "next/link";
import { useParams } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import { Box, Typography } from "@mui/material";
import SetWall from "./SetWall";
import { useSellableSets, useSetCatalogue, type SellableSet } from "./useArtSets";
import { artworkImageUrl } from "./useArtwork";
import { setMaterialOptions, setPath } from "./art-sets";
import { materialLabel } from "./SetMaterialPicker";
import { localized } from "./artwork-types";
import { formatMoney } from "../canvas/variant-price";
import { useShopCurrencyValue } from "src/hooks/useShopMarket";

/**
 * "Kompleti": the curated sets as one quiet section below the gallery grid.
 *
 * Each card shows the works side by side on a white ground at their smallest
 * shared size, so their relative proportions are true, with the set's total
 * "from" price. Renders nothing until the catalogue is in, and nothing if no
 * set can be sold.
 */
export default function ArtSetsRow() {
  const t = useTranslations("art");
  const { sets } = useSellableSets();
  if (!sets.length) return null;
  return (
    <Box
      component="section"
      sx={{ mt: { xs: 5, md: 7 }, mb: 5, pt: { xs: 4, md: 5 }, borderTop: "1px solid", borderColor: "divider" }}
      aria-labelledby="art-sets-row"
    >
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5, mb: 2, flexWrap: "wrap" }}>
        <Typography id="art-sets-row" component="h2" variant="h6" sx={{ fontWeight: 700 }}>
          {t("sets.rowTitle")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("sets.rowLede")}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: { xs: 2, md: 3 },
          overflowX: "auto",
          pb: 2,
          pr: 1,
          scrollSnapType: "x proximity",
          scrollbarWidth: "thin",
        }}
      >
        {sets.map((s) => (
          <Box
            key={s.set.slug}
            sx={{ flex: "0 0 auto", width: { xs: 250, md: 300 }, scrollSnapAlign: "start" }}
          >
            <ArtSetCard entry={s} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function ArtSetCard({ entry }: { entry: SellableSet }) {
  const t = useTranslations("art");
  const tEditor = useTranslations("editor");
  const { axes } = useSetCatalogue();
  const locale = useLocale();
  const params = useParams();
  const localeSegment = (params?.locale as string) ?? locale;
  const currency = useShopCurrencyValue();
  const { set, pieces, sizes } = entry;
  const smallest = sizes[0];
  // "Foto papir od X € · Platno od Y €": the set's total from, per material.
  const prices = setMaterialOptions(sizes, pieces.length, currency).map((o) =>
    t("sets.materialFrom", {
      material: materialLabel(
        o.material,
        tEditor as unknown as Parameters<typeof materialLabel>[1],
        axes,
        locale,
      ),
      price: formatMoney(o.from.amount, o.from.currency, locale),
    }),
  );
  const title = localized(set.title, locale);

  return (
    <Box
      component={NextLink}
      href={setPath(localeSegment, set.slug)}
      sx={{
        display: "block",
        color: "inherit",
        textDecoration: "none",
        "&:hover .setWall": { transform: "translateY(-3px)" },
        "&:focus-visible": { outline: "3px solid", outlineColor: "primary.main", outlineOffset: 4 },
      }}
    >
      <Box className="setWall" sx={{ transition: "transform .22s" }}>
        <SetWall
          thumbnail
          pieces={pieces.map((p, i) => ({
            src: artworkImageUrl(p.slug, "thumb", p.updatedAt),
            alt: "",
            widthMm: smallest.pieces[i].widthMm,
            heightMm: smallest.pieces[i].heightMm,
          }))}
          captions={{ sofa: "", bed: "", plain: "" }}
          sceneLabels={{ sofa: "", bed: "", plain: "" }}
        />
      </Box>
      <Box sx={{ pt: 1.25 }}>
        <Typography sx={{ fontWeight: 600, lineHeight: 1.3 }}>{title}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          {t("sets.pieceCount", { count: pieces.length })}
        </Typography>
        {/* One material per line, so a phone never breaks a price in two. */}
        {prices.map((line) => (
          <Typography key={line} variant="body2" sx={{ mt: 0.25, fontWeight: 700 }}>
            {line}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}
